/**
 * LocalStorageBackend - localStorage implementation for StorageService
 * 
 * Used when user is logged out. All operations are synchronous but wrapped
 * in Promises for interface consistency with CloudStorageBackend.
 */

import {
    STORAGE_KEY,
    SAVED_PATCHES_STORAGE_KEY,
    VERSIONS_STORAGE_KEY,
    MAX_VERSIONS
} from '../../constants/index.js';
import { generateId } from '../../utils/index.js';
import { clonePatchData } from '../../utils/serialization.js';

export function createLocalStorageBackend() {
    
    // ============= WORKING PATCH (AUTOSAVE) =============
    
    async function saveWorkingPatch(patchData) {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(patchData));
            return { success: true };
        } catch (e) {
            console.warn('[LocalBackend] saveWorkingPatch failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    async function loadWorkingPatch() {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (!saved) return { success: false, data: null };
            return { success: true, data: JSON.parse(saved) };
        } catch (e) {
            console.warn('[LocalBackend] loadWorkingPatch failed:', e);
            return { success: false, data: null, error: e.message };
        }
    }
    
    async function clearWorkingPatch() {
        try {
            localStorage.removeItem(STORAGE_KEY);
            return { success: true };
        } catch (e) {
            console.warn('[LocalBackend] clearWorkingPatch failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    // ============= NAMED PATCHES =============
    
    function _getPatchesArray() {
        try {
            const saved = localStorage.getItem(SAVED_PATCHES_STORAGE_KEY);
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            console.warn('[LocalBackend] _getPatchesArray failed:', e);
            return [];
        }
    }
    
    function _savePatchesArray(patches) {
        try {
            localStorage.setItem(SAVED_PATCHES_STORAGE_KEY, JSON.stringify(patches));
            return true;
        } catch (e) {
            console.warn('[LocalBackend] _savePatchesArray failed:', e);
            return false;
        }
    }
    
    async function savePatch(name, patchData, deviceCount = 0, connectionCount = 0, forceInsert = false) {
        try {
            const patches = _getPatchesArray();
            
            // For local storage, we use name as the identifier
            // forceInsert means create new even if same name exists (append number)
            let targetName = name;
            if (forceInsert) {
                // Check if name exists, append number if so
                let counter = 1;
                while (patches.some(p => p.name === targetName)) {
                    counter++;
                    targetName = `${name} (${counter})`;
                }
            }
            
            const existingIdx = patches.findIndex(p => p.name === targetName);
            
            const entry = {
                name: targetName,
                timestamp: Date.now(),
                deviceCount: deviceCount,
                connectionCount: connectionCount,
                data: clonePatchData(patchData)
            };
            
            if (existingIdx >= 0 && !forceInsert) {
                patches[existingIdx] = entry;
            } else {
                patches.push(entry);
            }
            
            if (_savePatchesArray(patches)) {
                return { success: true, id: targetName, name: targetName };
            } else {
                return { success: false, error: 'Failed to save to localStorage' };
            }
        } catch (e) {
            console.warn('[LocalBackend] savePatch failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    async function loadPatch(identifier) {
        try {
            const patches = _getPatchesArray();
            // For local, identifier is the name
            const patch = patches.find(p => p.name === identifier);
            
            if (!patch) {
                return { success: false, data: null, error: 'Patch not found' };
            }
            
            return {
                success: true,
                data: patch.data,
                name: patch.name,
                id: patch.name // For local, id === name
            };
        } catch (e) {
            console.warn('[LocalBackend] loadPatch failed:', e);
            return { success: false, data: null, error: e.message };
        }
    }
    
    async function deletePatch(identifier) {
        try {
            const patches = _getPatchesArray();
            const filtered = patches.filter(p => p.name !== identifier);
            
            if (filtered.length === patches.length) {
                return { success: false, error: 'Patch not found' };
            }
            
            if (_savePatchesArray(filtered)) {
                return { success: true };
            } else {
                return { success: false, error: 'Failed to save to localStorage' };
            }
        } catch (e) {
            console.warn('[LocalBackend] deletePatch failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    async function listPatches() {
        try {
            return _getPatchesArray().map(p => ({
                id: p.name,
                name: p.name,
                deviceCount: p.deviceCount || 0,
                connectionCount: p.connectionCount || 0,
                timestamp: p.timestamp || Date.now()
            }));
        } catch (e) {
            console.warn('[LocalBackend] listPatches failed:', e);
            return [];
        }
    }
    
    // ============= VERSIONS =============
    
    function _getVersionsArray() {
        try {
            const saved = localStorage.getItem(VERSIONS_STORAGE_KEY);
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            console.warn('[LocalBackend] _getVersionsArray failed:', e);
            return [];
        }
    }
    
    function _saveVersionsArray(versions) {
        try {
            localStorage.setItem(VERSIONS_STORAGE_KEY, JSON.stringify(versions));
            return true;
        } catch (e) {
            console.warn('[LocalBackend] _saveVersionsArray failed:', e);
            return false;
        }
    }
    
    async function saveVersion(name, patchData, deviceCount = 0, connectionCount = 0) {
        try {
            const versions = _getVersionsArray();
            
            const version = {
                id: generateId(),
                name: name || `Version ${versions.length + 1}`,
                timestamp: Date.now(),
                deviceCount: deviceCount,
                connectionCount: connectionCount,
                data: clonePatchData(patchData)
            };
            
            // Add to front (newest first)
            versions.unshift(version);
            
            // Keep only last MAX_VERSIONS
            while (versions.length > MAX_VERSIONS) {
                versions.pop();
            }
            
            if (_saveVersionsArray(versions)) {
                return { success: true, version };
            } else {
                return { success: false, error: 'Failed to save version' };
            }
        } catch (e) {
            console.warn('[LocalBackend] saveVersion failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    async function loadVersion(versionId) {
        try {
            const versions = _getVersionsArray();
            const version = versions.find(v => v.id === versionId);
            
            if (!version) {
                return { success: false, data: null, error: 'Version not found' };
            }
            
            return {
                success: true,
                data: version.data,
                name: version.name,
                id: version.id
            };
        } catch (e) {
            console.warn('[LocalBackend] loadVersion failed:', e);
            return { success: false, data: null, error: e.message };
        }
    }
    
    async function deleteVersion(versionId) {
        try {
            const versions = _getVersionsArray();
            const version = versions.find(v => v.id === versionId);
            
            if (!version) {
                return { success: false, error: 'Version not found' };
            }
            
            const filtered = versions.filter(v => v.id !== versionId);
            
            if (_saveVersionsArray(filtered)) {
                return { success: true, deletedName: version.name };
            } else {
                return { success: false, error: 'Failed to save versions' };
            }
        } catch (e) {
            console.warn('[LocalBackend] deleteVersion failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    async function listVersions() {
        try {
            return _getVersionsArray().map(v => ({
                id: v.id,
                name: v.name,
                timestamp: v.timestamp,
                deviceCount: v.deviceCount || 0,
                connectionCount: v.connectionCount || 0
                // data not included in list
            }));
        } catch (e) {
            console.warn('[LocalBackend] listVersions failed:', e);
            return [];
        }
    }
    
    async function renameVersion(versionId, newName) {
        try {
            const versions = _getVersionsArray();
            const version = versions.find(v => v.id === versionId);
            
            if (!version) {
                return { success: false, error: 'Version not found' };
            }
            
            version.name = newName;
            
            if (_saveVersionsArray(versions)) {
                return { success: true };
            } else {
                return { success: false, error: 'Failed to save versions' };
            }
        } catch (e) {
            console.warn('[LocalBackend] renameVersion failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    // ============= MIGRATION HELPERS =============
    
    /**
     * Get all local data for migration to cloud
     */
    async function getDataForMigration() {
        const workingPatchResult = await loadWorkingPatch();
        return {
            workingPatch: workingPatchResult.success ? workingPatchResult.data : null,
            patches: _getPatchesArray(),
            versions: _getVersionsArray()
        };
    }
    
    /**
     * Check if there's any local data to migrate
     */
    async function hasDataToMigrate() {
        const workingPatch = localStorage.getItem(STORAGE_KEY);
        const patches = _getPatchesArray();
        const versions = _getVersionsArray();
        
        return !!(workingPatch || patches.length > 0 || versions.length > 0);
    }
    
    /**
     * Clear all local data (after migration to cloud)
     */
    async function clearAll() {
        try {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(SAVED_PATCHES_STORAGE_KEY);
            localStorage.removeItem(VERSIONS_STORAGE_KEY);
            return { success: true };
        } catch (e) {
            console.warn('[LocalBackend] clearAll failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    // ============= PUBLIC API =============
    
    return {
        // Working patch
        saveWorkingPatch,
        loadWorkingPatch,
        clearWorkingPatch,
        
        // Named patches
        savePatch,
        loadPatch,
        deletePatch,
        listPatches,
        
        // Versions
        saveVersion,
        loadVersion,
        deleteVersion,
        listVersions,
        renameVersion,
        
        // Migration helpers
        getDataForMigration,
        hasDataToMigrate,
        clearAll
    };
}
